import { Component } from '@theme/component';

class CaseDeviceScroll extends Component {
  /** Share of the screenshot's height it travels across the viewport pass. */
  static SCROLL_FRACTION = 0.7;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number} rAF handle, 0 when nothing is scheduled. */
  #frame = 0;

  /** @type {boolean} */
  #listening = false;

  #handleScroll = () => this.#schedule();

  connectedCallback() {
    super.connectedCallback();

    if (!this.#isEnabled) return;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#startListening();
        else this.#stopListening();
      }
    });

    this.#observer.observe(this);

    const image = this.#image;
    if (image && !image.complete) {
      image.addEventListener('load', this.#handleScroll, { once: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = null;
    this.#stopListening();

    if (this.#frame) {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
    }
  }

  /** @returns {HTMLImageElement | null} */
  get #image() {
    return this.refs.image ?? this.querySelector('img');
  }

  /** @returns {boolean} */
  get #isEnabled() {
    if (!this.#image) return false;
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #startListening() {
    if (this.#listening) return;
    this.#listening = true;

    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('resize', this.#handleScroll, { passive: true });
    this.#schedule();
  }

  #stopListening() {
    if (!this.#listening) return;
    this.#listening = false;

    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleScroll);
  }

  #schedule() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  }

  #update() {
    const image = this.#image;
    if (!image) return;

    const hidden = Math.max(0, image.offsetHeight - this.clientHeight);
    const travel = Math.min(image.offsetHeight * CaseDeviceScroll.SCROLL_FRACTION, hidden);

    if (travel === 0) {
      image.style.removeProperty('--case-devices-scroll');
      return;
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const { top, height } = this.getBoundingClientRect();
    const span = viewportHeight + height;
    const progress = span > 0 ? clamp((viewportHeight - top) / span, 0, 1) : 0;

    image.style.setProperty('--case-devices-scroll', `${(-progress * travel).toFixed(2)}px`);
  }
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

if (!customElements.get('case-device-scroll')) {
  customElements.define('case-device-scroll', CaseDeviceScroll);
}
